import { useEffect, useRef } from 'react';

export const SKYFIRE_STRIKE_ORDER_ID = 275;
export const SKYFIRE_STRIKE_ANIMATION_VARIANT = 'skyfire_strike_blue_stick';

function captureDroneCenterPixel(zone, index, instanceId) {
  if (typeof document === 'undefined') {
    return null;
  }
  const byInstance =
    instanceId &&
    document.querySelector(
      `[data-drone-zone="${zone}"][data-instance-id="${String(instanceId)}"]`
    );
  const el =
    byInstance ||
    document.querySelector(`[data-drone-zone="${zone}"][data-drone-index="${index}"]`);
  if (!el) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

export function skyfireChargeWasSpent(prevDrone, currDrone) {
  if (!prevDrone || Number(prevDrone.id) !== SKYFIRE_STRIKE_ORDER_ID) {
    return false;
  }
  if (prevDrone.charges === undefined) {
    return false;
  }
  if (currDrone) {
    return currDrone.charges !== undefined && currDrone.charges < prevDrone.charges;
  }
  // Drone removed on final charge (tank deploy path) — still fired this activation
  return prevDrone.charges > 0;
}

/**
 * When Skyfire Strike (275) spends a charge, play a deploy-style beam from the drone slot to the damaged general.
 */
export function useSkyfireStrikeAnimations(gameState, viewerUserId, registerAnimation) {
  const prevGameStateRef = useRef(null);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null || !registerAnimation) {
      prevGameStateRef.current = gameState;
      return;
    }

    const prevGameState = prevGameStateRef.current;
    if (!prevGameState?.state) {
      prevGameStateRef.current = gameState;
      return;
    }

    const playerIds = gameState.players || Object.keys(gameState.state);

    for (const playerId of playerIds) {
      const prevDrones = prevGameState.state[playerId]?.drones || [];
      const currDrones = gameState.state[playerId]?.drones || [];

      prevDrones.forEach((prevDrone) => {
        if (Number(prevDrone.id) !== SKYFIRE_STRIKE_ORDER_ID) {
          return;
        }

        const currDrone = currDrones.find((d) => d.instanceId === prevDrone.instanceId);
        if (!skyfireChargeWasSpent(prevDrone, currDrone)) {
          return;
        }

        const droneIndex = prevDrones.findIndex((d) => d.instanceId === prevDrone.instanceId);
        if (droneIndex < 0) {
          return;
        }

        const isViewerOwner = String(playerId) === String(viewerUserId);
        const fromZone = isViewerOwner ? 'your_drone' : 'opponent_drone';
        const toGeneralPlayerId = isViewerOwner ? 'opponent' : 'your';
        const cachedSourcePixel = captureDroneCenterPixel(
          fromZone,
          droneIndex,
          prevDrone.instanceId
        );

        registerAnimation({
          fromSlot: {
            zone: fromZone,
            index: droneIndex,
            playerId: isViewerOwner ? 'your' : 'opponent',
            instanceId: prevDrone.instanceId,
            cachedSourcePixel,
          },
          toSlot: {
            zone: 'general',
            index: null,
            playerId: toGeneralPlayerId,
          },
          animationType: 'opponent',
          deployAnimationVariant: SKYFIRE_STRIKE_ANIMATION_VARIANT,
          animationKey: `skyfire-${prevDrone.instanceId}-${Date.now()}`,
        });
      });
    }

    prevGameStateRef.current = gameState;
  }, [gameState, viewerUserId, registerAnimation]);
}
